import { logger } from './logger';
import { createLogContext } from './loggerUtils';
import { BatchedStorageManager } from './batchedStorageManager';
import { STORAGE_KEYS, isFeatureKey } from '../constants/storageKeys';
import { StorageData, StorageKey, StorageDataTypeMap } from '../types/storage';
import {
  serializeForStorage,
  deserializeFromStorage,
  validatePromptData,
  validateFolderData,
} from './storageUtils';
import { getKvStore } from '../infrastructure/platform';

/**
 * Interface for storage operations
 */
interface StorageInterface {
  // Type-safe set method for known storage keys
  set<K extends StorageKey>(key: K, value: StorageDataTypeMap[K]): Promise<void>;
  // Generic set method for any key
  set(key: string, value: any): Promise<void>;

  // Type-safe get method for known storage keys
  get<K extends StorageKey>(key: K): Promise<StorageDataTypeMap[K] | null>;
  // Generic get method for any key
  get(key: string): Promise<any>;

  // Get value with timestamp metadata
  getWithTimestamp<T = any>(key: string): Promise<StorageData<T>>;

  // Other storage operations
  remove(key: string): Promise<void>;
  clear(): Promise<void>;
  getAll(): Promise<Record<string, StorageData<any>>>;
  setMultiple(updates: Record<string, any>): Promise<void>;
  _isInitialized(): boolean;
}

/**
 * Storage implementation over the platform's key-value backend
 * (chrome.storage.local on the extension, IndexedDB on the web — ADR-012).
 */
class ChromeStorage implements StorageInterface {
  private initialized = false;
  private storageManager: BatchedStorageManager | null = null;

  constructor() {
    if (getKvStore().available) {
      this.initialized = true;
      this.storageManager = BatchedStorageManager.getInstance({
        batchDelay: 50, // 50ms delay to batch operations
        maxBatchSize: 100, // Maximum operations in a batch
        retryAttempts: 3, // Number of retry attempts
        retryDelay: 1000, // 1 second between retries
      });
    }
  }

  // Implementation of the set method
  async set(key: string, value: any): Promise<void> {
    if (!this.initialized) {
      logger.warning('Storage not initialized', {
        module: 'storage',
        function: 'set',
        key,
      });
      return;
    }

    try {
      const ctx = createLogContext('set', { key });
      logger.debug('Setting storage value', ctx);

      // Validate data based on key type. On failure we REJECT (throw) rather
      // than persisting anyway — otherwise validation is cosmetic and corrupt
      // data still lands in storage.
      if (key === STORAGE_KEYS.PROMPTS && !validatePromptData(value)) {
        logger.warning('Refusing to store invalid prompt data', {
          ...ctx,
          dataType: typeof value,
        });
        throw new Error('Refusing to store invalid prompt data');
      } else if (key === STORAGE_KEYS.FOLDERS) {
        // If it's not an array, try to fix it
        if (!Array.isArray(value)) {
          if (typeof value === 'object' && value !== null) {
            // Try to extract array from object if possible
            if (Array.isArray(value.data)) {
              value = value.data;
            } else if (Array.isArray(Object.values(value)[0])) {
              value = Object.values(value)[0];
            }
          }
        }

        const isValid = validateFolderData(value);
        if (!isValid) {
          logger.warning('Refusing to store invalid folder data', {
            ...ctx,
            dataType: typeof value,
            isArray: Array.isArray(value),
            value: JSON.stringify(value),
          });
          throw new Error('Refusing to store invalid folder data');
        }
      }

      const storageValue = serializeForStorage(value);
      await this.storageManager?.set(key, storageValue);

      logger.debug('Storage value set successfully', ctx);
    } catch (error) {
      const ctx = createLogContext('set', { key });
      logger.error(
        'Failed to set storage value',
        error instanceof Error ? error : new Error('Unknown error'),
        ctx
      );
      throw error;
    }
  }

  // Implementation of the get method
  async get(key: string): Promise<any> {
    if (!this.initialized) {
      logger.warning('Storage not initialized', {
        module: 'storage',
        function: 'get',
        key,
      });
      return null;
    }

    try {
      const ctx = createLogContext('get', { key });
      logger.debug('Getting storage value', ctx);

      const result = await this.storageManager?.get(key);
      const data = deserializeFromStorage(result);

      // Special handling for folders to ensure we always return an array
      if (key === STORAGE_KEYS.FOLDERS) {
        return Array.isArray(data) ? data : [];
      }

      return data;
    } catch (error) {
      const ctx = createLogContext('get', { key });
      logger.error(
        'Failed to get storage value',
        error instanceof Error ? error : new Error('Unknown error'),
        ctx
      );
      throw error;
    }
  }

  async getWithTimestamp<T = any>(key: string): Promise<StorageData<T>> {
    if (!this.initialized) {
      logger.warning('Storage not initialized', {
        module: 'storage',
        function: 'getWithTimestamp',
        key,
      });
      return { data: null as unknown as T, lastSync: '' };
    }

    try {
      const ctx = createLogContext('getWithTimestamp', { key });
      logger.debug('Getting storage value with timestamp', ctx);

      // Cast the result to the expected type
      const result = (await this.storageManager?.get(key)) as StorageData<T> | null | undefined;
      return result ?? { data: null as unknown as T, lastSync: '' };
    } catch (error) {
      const ctx = createLogContext('getWithTimestamp', { key });
      logger.error(
        'Failed to get storage value with timestamp',
        error instanceof Error ? error : new Error('Unknown error'),
        ctx
      );
      throw error;
    }
  }

  async remove(key: string): Promise<void> {
    if (!this.initialized) {
      logger.warning('Storage not initialized', {
        module: 'storage',
        function: 'remove',
        key,
      });
      return;
    }

    try {
      const ctx = createLogContext('remove', { key });
      logger.debug('Removing storage value', ctx);

      await this.storageManager?.remove(key);

      logger.debug('Storage value removed successfully', ctx);
    } catch (error) {
      const ctx = createLogContext('remove', { key });
      logger.error(
        'Failed to remove storage value',
        error instanceof Error ? error : new Error('Unknown error'),
        ctx
      );
      throw error;
    }
  }

  async clear(): Promise<void> {
    if (!this.initialized) {
      logger.warning('Storage not initialized', {
        module: 'storage',
        function: 'clear',
      });
      return;
    }

    try {
      const ctx = createLogContext('clear');
      logger.debug('Clearing all storage', ctx);

      // Flush any pending operations before clearing
      await this.storageManager?.flush();
      await getKvStore().clear();

      logger.debug('Storage cleared successfully', ctx);
    } catch (error) {
      const ctx = createLogContext('clear');
      logger.error(
        'Failed to clear storage',
        error instanceof Error ? error : new Error('Unknown error'),
        ctx
      );
      throw error;
    }
  }

  async getAll(): Promise<Record<string, StorageData<any>>> {
    if (!this.initialized) {
      logger.warning('Storage not initialized', {
        module: 'storage',
        function: 'getAll',
      });
      return {};
    }

    try {
      const ctx = createLogContext('getAll');
      logger.debug('Getting all storage values', ctx);

      const result = await getKvStore().get(null);
      return result as Record<string, StorageData<any>>;
    } catch (error) {
      const ctx = createLogContext('getAll');
      logger.error(
        'Failed to get all storage values',
        error instanceof Error ? error : new Error('Unknown error'),
        ctx
      );
      throw error;
    }
  }

  async setMultiple(updates: Record<string, any>): Promise<void> {
    if (!this.initialized) {
      logger.warning('Storage not initialized', {
        module: 'storage',
        function: 'setMultiple',
      });
      return;
    }

    try {
      const ctx = createLogContext('setMultiple', { keys: Object.keys(updates) });
      logger.debug('Setting multiple storage values', ctx);

      // Validate and format updates
      const timestampedUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
        // Skip null or undefined values
        if (value === null || value === undefined) {
          logger.debug('Skipping null/undefined value', {
            ...ctx,
            key,
          });
          return acc;
        }

        // Extract the actual data value
        const dataValue = value.data !== undefined ? value.data : value;

        // Handle theme data specially
        if (key === STORAGE_KEYS.THEME) {
          const themeValue = typeof dataValue === 'string' ? dataValue : 'light';
          logger.debug('Processing theme value', {
            ...ctx,
            theme: themeValue,
          });
          return {
            ...acc,
            [key]: serializeForStorage(themeValue),
          };
        }

        // Handle arrays (prompts, folders)
        if (key === STORAGE_KEYS.PROMPTS) {
          const promptsArray = Array.isArray(dataValue) ? dataValue : [];
          if (!validatePromptData(promptsArray)) {
            // Skip rather than persist invalid data — leaves the existing
            // stored value untouched instead of overwriting it with garbage.
            logger.warning('Skipping invalid prompt data in setMultiple', {
              ...ctx,
              key,
            });
            return acc;
          }
          logger.debug('Processing prompts array', {
            ...ctx,
            length: promptsArray.length,
          });
          return {
            ...acc,
            [key]: serializeForStorage(promptsArray),
          };
        }

        if (key === STORAGE_KEYS.FOLDERS) {
          const foldersArray = Array.isArray(dataValue) ? dataValue : [];
          if (!validateFolderData(foldersArray)) {
            // Skip rather than persist invalid data.
            logger.warning('Skipping invalid folder data in setMultiple', {
              ...ctx,
              key,
            });
            return acc;
          }
          logger.debug('Processing folders array', {
            ...ctx,
            length: foldersArray.length,
          });
          return {
            ...acc,
            [key]: serializeForStorage(foldersArray),
          };
        }

        // Handle feature flags
        if (isFeatureKey(key)) {
          const featureValue = Boolean(dataValue);
          logger.debug('Processing feature flag', {
            ...ctx,
            key,
            value: featureValue,
          });
          return {
            ...acc,
            [key]: serializeForStorage(featureValue),
          };
        }

        // Default case: store as-is with timestamp
        logger.debug('Processing default value', {
          ...ctx,
          key,
        });
        return {
          ...acc,
          [key]: serializeForStorage(dataValue),
        };
      }, {});

      await this.storageManager?.setMultiple(timestampedUpdates);

      logger.debug('Multiple storage values set successfully', {
        ...ctx,
        keys: Object.keys(timestampedUpdates),
      });
    } catch (error) {
      const ctx = createLogContext('setMultiple');
      logger.error(
        'Failed to set multiple storage values',
        error instanceof Error ? error : new Error('Unknown error'),
        ctx
      );
      throw error;
    }
  }

  _isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Gets storage metrics
   */
  getMetrics() {
    if (!this.initialized) return null;
    return this.storageManager?.getMetrics() ?? null;
  }
}

export const storage = new ChromeStorage();
